<template>
  <Button
    type="button"
    text
    severity="secondary"
    icon="pi pi-angle-down"
    iconPos="right"
    :label="menuLabel"
    :aria-label="ariaLabel || menuLabel"
    aria-haspopup="true"
    :aria-controls="menuId"
    v-bind="$attrs"
    @click="toggleAdminMenu"
  />

  <Menu :id="menuId" ref="adminMenu" :model="adminMenuItems" :popup="true" class="w-auto px-0">
    <template #item="{ label, item, props }">
      <router-link v-slot="{ href, navigate }" :to="item.route" custom>
        <a :href="href" v-bind="props.action" @click="navigate">
          <span v-bind="props.icon" />
          <span v-bind="props.label">{{ label }}</span>
        </a>
      </router-link>
    </template>
  </Menu>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { RouterLink } from "vue-router";
import type { GroupMenuItem, MenuItem, UserPermissions } from "@/models";
import { useAppContextStore } from "@/core/store";
import { useEnvironmentFlags } from "@/core/composables/useEnvironmentFlags";

interface Props {
  menuLabel?: string;
  ariaLabel?: string;
}

const { menuLabel = "Admin", ariaLabel = "" } = defineProps<Props>();

// ---- context & permissions ----
const contextStore = useAppContextStore();
const context = computed(() => contextStore.context);

const {
  dataRequestsEnabled,
  dataTransferEnabled,
  managersNavEnabled,
  regionalLeadersEnabled,
  importsEnabled,
  notificationsEnabled,
  flagAdministrationEnabled,
  apiKeyAdminEnabled,
  skillAdministrationEnabled,
} = useEnvironmentFlags();

const perms = computed<UserPermissions>(() => {
  return (context.value?.user?.permissions ?? {}) as UserPermissions;
});

// ---- state ----
const adminMenu = ref<any>(null);
const menuId = "admin-menu";

// ---- typed helper to remove false/null/undefined from conditional arrays ----
const compact = <T,>(arr: Array<T | null | undefined | false>): T[] =>
  arr.filter((x): x is T => Boolean(x));

// ---- normalize booleans once (no double-negation / redundant coercion) ----
const capabilities = computed(() => {
  const p = perms.value.page ?? {};

  return {
    // Data
    employees: p.employeesPage === true,
    managers: managersNavEnabled.value && p.managersPage === true,
    regionalLeaders: regionalLeadersEnabled.value && p.regionalLeadersPage === true,
    resumeTracker: p.resumeTrackerPage === true,
    reconcileData: p.reconcileDataPage === true,
    transferData: dataTransferEnabled.value && p.transferDataPage === true,
    dataRequests: dataRequestsEnabled.value && p.dataRequestsPage === true,
    scheduledAI: p.scheduledForAIScoringPage === true,
    imports: importsEnabled.value && p.importsPage === true,
    notifications: notificationsEnabled.value && p.notificationsPage === true,
    resumeFlagAdministration:
      flagAdministrationEnabled.value && p.resumeFlagAdministrationPage === true,

    // Uploads
    employeeUpload: p.employeeUploadPage === true,

    // Configuration
    configuration: p.configurationPage === true,
    skillAdministration: skillAdministrationEnabled.value && p.skillAdministrationPage === true,
    resumeExclusion: p.resumeExclusionPage === true,
    userManagementFine: p.permissionManagementPage === true,
    apiKeys: apiKeyAdminEnabled.value && p.apiKeyPage === true,
  };
});

// ---- menu model (reactive to permissions + feature flags) ----
const adminMenuItems = computed<GroupMenuItem[]>(() => {
  const c = capabilities.value;

  const dataItems: MenuItem[] = compact<MenuItem>([
    // c.imports && { label: "Imports", icon: "pi pi-fw pi-file-import", route: "/imports" },
    c.employees && { label: "Employees", icon: "pi pi-fw pi-users", route: "/employees" },
    c.managers && { label: "Managers", icon: "pi pi-fw pi-users", route: "/managers" },
    c.regionalLeaders && {
      label: "Regional Leaders",
      icon: "pi pi-fw pi-users",
      route: "/regional-leaders",
    },
    c.resumeTracker && {
      label: "Resume Tracker",
      icon: "pi pi-fw pi-calendar-plus",
      route: "/resume-tracker",
    },
    // c.reconcileData && {
    //   label: "Reconcile Data",
    //   icon: "pi pi-fw pi-search-minus",
    //   route: "/reconcile-data",
    // },
    // c.transferData && {
    //   label: "Transfer Data",
    //   icon: "pi pi-fw pi-arrow-right-arrow-left",
    //   route: "/transfer-data",
    // },
    // c.dataRequests && {
    //   label: "Data Requests",
    //   icon: "pi pi-fw pi-ticket",
    //   route: "/data-request",
    //   badge: "has-badge",
    // },
    // c.scheduledAI && {
    //   label: "Scheduled for AI",
    //   icon: "pi pi-fw pi-android",
    //   route: "/ai-scheduled",
    // },
    // c.resumeFlagAdministration && {
    //   label: "Resume Flag Administration",
    //   icon: "pi pi-fw pi-flag",
    //   route: "/resume-flags",
    // },
  ]);

  // const uploadItems: MenuItem[] = compact<MenuItem>([
  //   c.employeeUpload && {
  //     label: "Employee Upload",
  //     icon: "pi pi-fw pi-upload",
  //     route: "/employee-upload",
  //   },
  // ]);

  const configItems: MenuItem[] = compact<MenuItem>([
    // c.configuration && { label: "Configuration", icon: "pi pi-fw pi-cog", route: "/configuration" },

    // c.skillAdministration && {
    //   label: "Skill Administration",
    //   icon: "pi pi-fw pi-sparkles",
    //   route: "/skill-administration",
    // },
    c.configuration && {
      label: "Configuration",
      icon: "pi pi-fw pi-cog",
      route: "/configuration",
    },
    // c.notifications && {
    //   label: "System Notifications",
    //   icon: "pi pi-fw pi-megaphone",
    //   route: "/notifications",
    // },
    // c.resumeExclusion && {
    //   label: "Resume Exclusion",
    //   icon: "pi pi-fw pi-ban",
    //   route: "/resume-exclusion",
    // },
    // c.userManagementFine && {
    //   label: "Users & Permissions",
    //   icon: "pi pi-fw pi-unlock",
    //   route: "/permission-management",
    // },
    // c.apiKeys && {
    //   label: "API Keys",
    //   icon: "pi pi-fw pi-key",
    //   route: "/api-keys",
    // },
  ]);

  // Only include groups that have visible items
  return compact<GroupMenuItem>([
    dataItems.length ? { label: "Data", items: dataItems } : false,
    // uploadItems.length ? { label: "Uploads", items: uploadItems } : false,
    configItems.length ? { label: "Configuration", items: configItems } : false,
  ]);
});

// ---- actions ----
const toggleAdminMenu = (event: MouseEvent) => {
  adminMenu.value?.toggle(event);
};
</script>

<style scoped lang="scss">
/* keep styles local to this component (no global ul/a leakage) */
:deep(ul) {
  padding-left: 0;
}

:deep(a) {
  text-decoration: none !important;
}
</style>
